import React, { useRef, useState } from "react";
import { Controller, Control, FieldErrors, UseFormSetValue, UseFormTrigger } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styles from "./multi-step-form.module.scss";
import { FormData } from "./types";
import PhoneInput from "../../../../../common/ekedc-components/phone-number-input/PhoneInput";
import { validatePhoneNumber } from "../../../validators";
import { useTenant } from "../../../../../tenant/branding/useTenant";

interface Props {
	control: Control<FormData>;
	errors: FieldErrors<FormData>;
	setValue: UseFormSetValue<FormData>;
	trigger: UseFormTrigger<FormData>;
	onNext: () => void;
}

export const Step2ContactInfo: React.FC<Props> = ({ control, errors, setValue, trigger, onNext }) => {
	const { t } = useTranslation();
	const { countryCode } = useTenant();
	const [phoneTouched, setPhoneTouched] = useState(false);
	// PhoneInput fires onChange on mount with the default country code — skip that first call
	const phoneInitRef = useRef(false);

	const handleNext = async () => {
		setPhoneTouched(true);
		if (await trigger(["email", "phone"])) onNext();
	};

	return (
		<div className={styles.stepContent}>
			<div className={`${styles.card} ${styles.felxGapSection}`}>
				<div>
					<div className={styles.cardHeader}>
						<span className={styles.cardLabel}>{t("common.labels.emailAddress")}</span>
					</div>
					<Controller
						name="email"
						control={control}
						rules={{
							required: t("auth.web.emailIsRequired"),
							pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: t("auth.web.invalidEmailAddress") },
						}}
						render={({ field }) => (
							<>
								<input {...field} type="email" placeholder={t("auth.web.emailPlaceholder")} className={`${styles.cardInput} ${errors.email ? styles.inputError : ""}`} />
								{errors.email && <span className={styles.fieldError}>{errors.email.message}</span>}
							</>
						)}
					/>
				</div>
				<div>
					<div className={styles.cardHeader}>
						<span className={styles.cardLabel}>{t("common.labels.phoneNumber")}</span>
					</div>
					<div className={styles.phoneWrapper}>
						<Controller
							name="phone"
							control={control}
							rules={{
								validate: val => {
									if (!val) return t("errors.api.phoneNumberRequired");
									return validatePhoneNumber(val);
								},
							}}
							render={({ field }) => (
								<PhoneInput
									label=""
									value={field.value}
									onChangeParts={(localNumber, dialCode) => {
										field.onChange(localNumber);
										if (dialCode) setValue("countryDialCode", dialCode);
										if (phoneInitRef.current) setPhoneTouched(true);
										else phoneInitRef.current = true;
									}}
									onBlur={field.onBlur}
									placeholder={t("auth.web.phonePlaceholder")}
									error={phoneTouched ? errors.phone?.message : undefined}
									showCountrySelector={false}
									defaultCountry={countryCode}
									lockCountry
								/>
							)}
						/>
					</div>
				</div>
				<p className={styles.footNote}>
					{t("auth.verificationCodeWillBeSent")}{" "}
					<span className={styles.learnMore}>{t("auth.learnMore")}</span>
				</p>
			</div>

			<div className={styles.buttonSection}>
				<button type="button" className={styles.continueButton} onClick={handleNext}>
					{t("common.actions.continueKey")}
				</button>
			</div>
		</div>
	);
};
